import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SearchHeader } from './SearchHeader';
import { SearchResults } from './SearchResults';
import { PropertyDetailsModal } from '../PropertyDetails/PropertyDetailsModal';
import { useProperties } from '../../hooks/useProperties';
import { PropertySearchFilters } from '../../types/Property';
import { apiService } from '../../services/ApiService';
import './SearchPage.css';

interface SearchPageProps {
  onBackToHome?: () => void;
}

export const SearchPage: React.FC<SearchPageProps> = ({ onBackToHome }) => {
  const location = useLocation();
  const searchData = location.state?.searchData;
  const codigoFromUrl = location.state?.codigo;
  
  // Inicializar searchLocation: mostrar apenas o bairro se houver, senão vazio
  const getInitialSearchLocation = () => {
    if (searchData?.bairro && searchData.bairro.trim() !== '') {
      return searchData.bairro.trim();
    }
    return '';
  };
  
  const [searchLocation, setSearchLocation] = useState(getInitialSearchLocation());
  const [showFilters, setShowFilters] = useState(false);
  const [selectedPropertyData, setSelectedPropertyData] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Estados para busca por código
  const [singleProperty, setSingleProperty] = useState<any>(null);
  const [singlePropertyLoading, setSinglePropertyLoading] = useState(false);
  
  // Estado local para filtros inline - inicializar primeiro
  const [localFilters, setLocalFilters] = useState({
    cidade: 'Todas',
    bairro: '',
    valorMaximo: '',
    quartos: '',
    banheiros: '',
    vagas: '',
    tipoImovel: '',
    area: '',
    finalidade: '',
  });
  
  // Estados para busca normal - usar cidade do localFilters
  const [filters, setFilters] = useState<PropertySearchFilters>({
    bairro: '',
    estado: 'SC',
    page: 1,
    limit: 100,
  });
  
  // Flag para controlar se devemos executar busca após atualizar filtros
  const [shouldSearchAfterFiltersUpdate, setShouldSearchAfterFiltersUpdate] = useState(false);

  // Estado para ordenação
  const [sortOrder, setSortOrder] = useState<'relevantes' | 'maior-valor' | 'menor-valor'>('relevantes');
  
  // Estado para controlar a tab ativa (alugar ou comprar)
  const [activeTab, setActiveTab] = useState<'alugar' | 'comprar'>('alugar');
  
  // Inicializar useProperties - não fazer busca automática inicial
  // A busca será feita apenas quando o usuário interagir ou quando houver searchData
  const initialFiltersForHook = codigoFromUrl ? {} : (searchData ? {} : {});
  
  const { imoveis, loading, pagination, searchImoveis } = useProperties(initialFiltersForHook);

  // Buscar imóvel por código se fornecido
  useEffect(() => {
    if (codigoFromUrl) {
      const fetchPropertyByCode = async () => {
        setSinglePropertyLoading(true);
        try {
          const imovel = await apiService.getImovelByCodigo(codigoFromUrl);
          if (imovel) {
            setSingleProperty(imovel);
            setSearchLocation(`Imóvel Cód: ${codigoFromUrl}`);
          } else {
            setSingleProperty(null);
          }
        } catch (err: any) {
          console.error('❌ Erro ao buscar imóvel por código:', err);
          setSingleProperty(null);
        } finally {
          setSinglePropertyLoading(false);
        }
      };
      fetchPropertyByCode();
    }
  }, [codigoFromUrl]);

  // Aplicar dados da busca normal quando recebidos
  useEffect(() => {
    if (codigoFromUrl) return; // Não aplicar se há código
    
    // Aguardar um pouco para garantir que o componente está montado
    if (searchData) {
      if (searchData.buscarTodos) {
        const cidadeSelecionada = localFilters.cidade || 'Todas';
        setSearchLocation('');
        const newFilters: PropertySearchFilters = {
          bairro: '',
          estado: 'SC',
          page: 1,
          limit: 100,
        };
        
        // Só adicionar cidade se não for "Todas"
        if (cidadeSelecionada && cidadeSelecionada !== 'Todas') {
          newFilters.cidade = cidadeSelecionada;
        }
        
        setLocalFilters({
          cidade: cidadeSelecionada,
          bairro: '',
          valorMaximo: '',
          quartos: '',
          banheiros: '',
          vagas: '',
          tipoImovel: '',
          area: '',
          finalidade: '',
        });
        setFilters(newFilters);
        // Marcar que devemos executar a busca após os filtros serem atualizados
        setShouldSearchAfterFiltersUpdate(true);
      } else {
        const bairro = searchData.bairro?.trim() || '';
        const cidade = searchData.cidade?.trim() || 'Todas';
        const estado = searchData.estado?.trim() || 'SC';
        const valorMaximo = searchData.valorMaximo?.trim() || '';
        const quartos = searchData.quartos?.trim() || '';
        const tipoImovel = searchData.tipoImovel?.trim() || searchData.tipo_imovel?.trim() || '';
        
        // Detectar finalidade: primeiro verificar se há campo finalidade direto, senão inferir dos campos booleanos
        let finalidade = '';
        if (searchData.finalidade && searchData.finalidade.trim() !== '') {
          finalidade = searchData.finalidade.trim();
        } else if (searchData.apenas_aluguel) {
          finalidade = 'aluguel';
        } else if (searchData.apenas_venda) {
          finalidade = 'venda';
        } else if (searchData.aluguel_e_venda) {
          finalidade = 'ambos';
        }
        
        // Mostrar apenas o bairro se houver
        const newLocation = bairro && bairro.trim() !== '' 
          ? bairro 
          : '';
        setSearchLocation(newLocation);
        
        const quartosValue = quartos ? (typeof quartos === 'string' ? quartos.replace('+', '') : quartos.toString()) : '';
        
        // Preparar filtros locais
        const newLocalFilters = {
          cidade: cidade || 'Todas',
          bairro: bairro || '',
          valorMaximo: valorMaximo || '',
          quartos: quartosValue,
          banheiros: '',
          vagas: '',
          tipoImovel: tipoImovel || '',
          area: '',
          finalidade: finalidade,
        };
        
        // Preparar filtros para busca - criar objeto limpo apenas com os filtros válidos
        const newFilters: PropertySearchFilters = {
          estado: estado || 'SC',
          page: 1,
          limit: 100,
        };
        
        // Só adicionar cidade se não for "Todas" ou vazio
        if (cidade && cidade.trim() !== '' && cidade !== 'Todas') {
          newFilters.cidade = cidade;
        }
        
        // Adicionar filtros opcionais apenas se tiverem valores válidos
        if (bairro && bairro.trim() !== '') {
          newFilters.bairro = bairro;
        }
        
        if (tipoImovel && tipoImovel.trim() !== '') {
          newFilters.tipo_imovel = tipoImovel;
        }
        
        if (valorMaximo && valorMaximo.trim() !== '') {
          const valorMax = parseFloat(valorMaximo);
          if (!isNaN(valorMax) && valorMax > 0) {
            newFilters.valorMaximo = valorMaximo;
            newFilters.valor_max = valorMax;
          }
        }
        
        if (quartosValue && quartosValue.trim() !== '') {
          const quartosNum = parseInt(quartosValue);
          if (!isNaN(quartosNum) && quartosNum > 0) {
            newFilters.quartos_min = quartosNum;
          }
        }
        
        // Adicionar filtros de finalidade se houver
        if (finalidade) {
          if (finalidade === 'aluguel') {
            newFilters.apenas_aluguel = true;
          } else if (finalidade === 'venda') {
            newFilters.apenas_venda = true;
          } else if (finalidade === 'ambos') {
            newFilters.aluguel_e_venda = true;
          }
        }
        
        // Primeiro atualizar os filtros locais
        setLocalFilters(newLocalFilters);
        // Depois atualizar os filtros do estado
        setFilters(newFilters);
        // Marcar que devemos executar a busca após os filtros serem atualizados
        setShouldSearchAfterFiltersUpdate(true);
      }
    }
  }, [searchData, codigoFromUrl]);
  
  // Executar busca quando os filtros forem atualizados e a flag estiver ativa
  useEffect(() => {
    if (shouldSearchAfterFiltersUpdate && filters && !codigoFromUrl) {
      // Resetar a flag primeiro para evitar re-execução
      setShouldSearchAfterFiltersUpdate(false);
      
      // Garantir que a cidade sempre seja enviada do localFilters (valor atual do seletor)
      const filtersToUse: PropertySearchFilters = {
        ...filters,
        estado: filters.estado || 'SC',
      };
      
      // Só adicionar cidade se não for "Todas"
      if (localFilters.cidade && localFilters.cidade !== 'Todas') {
        filtersToUse.cidade = localFilters.cidade;
      } else if (filters.cidade && filters.cidade !== 'Todas') {
        filtersToUse.cidade = filters.cidade;
      } else {
        // Se for "Todas" ou vazio, não adicionar filtro de cidade
        delete filtersToUse.cidade;
      }
      
      // Executar busca diretamente com os filtros passados
      // Passar todos os filtros explicitamente - o searchImoveis agora usa apenas os filtros passados
      // quando há propriedades explícitas, ignorando os filtros internos
      // NÃO atualizar os filtros internos aqui para evitar que dispare o useEffect do hook
      // Os filtros internos serão atualizados apenas quando o usuário interagir com os filtros na página
      searchImoveis(filtersToUse);
    }
  }, [filters, shouldSearchAfterFiltersUpdate, searchImoveis, codigoFromUrl, localFilters.cidade]);

  const handleSearch = (searchFilters: any) => {
    if (codigoFromUrl) return;
    
    const hasLocationFilter = searchFilters.location && searchFilters.location.trim() !== '';
    const hasLocalFilters = Object.values(localFilters).some((value: any) => {
      if (typeof value === 'string') {
        return value.trim() !== '';
      }
      return value !== '';
    });
    
    if (!hasLocationFilter && !hasLocalFilters) {
      setSearchLocation('');
      const newFilters: PropertySearchFilters = {
        estado: 'SC',
        page: 1,
        limit: 100,
      };
      
      // Só adicionar cidade se não for "Todas"
      if (localFilters.cidade && localFilters.cidade !== 'Todas') {
        newFilters.cidade = localFilters.cidade;
      }
      setLocalFilters({
        cidade: localFilters.cidade || 'Todas',
        bairro: '',
        valorMaximo: '',
        quartos: '',
        banheiros: '',
        vagas: '',
        tipoImovel: '',
        area: '',
        finalidade: '',
      });
      setFilters(newFilters);
      searchImoveis(newFilters);
      return;
    }
    
    let newFilters: PropertySearchFilters = { ...filters };
    
    // Sempre usar a cidade do seletor (localFilters.cidade)
    newFilters.estado = 'SC';
    
    // Só adicionar cidade se não for "Todas"
    if (localFilters.cidade && localFilters.cidade !== 'Todas') {
      newFilters.cidade = localFilters.cidade;
    }
    
    // Se o usuário digitou algo no campo de localização, tratar como bairro/endereço
    if (hasLocationFilter) {
      const locationText = searchFilters.location.trim();
      if (locationText) {
        newFilters.bairro = locationText;
        setSearchLocation(locationText);
      } else {
        newFilters.bairro = '';
        setSearchLocation('');
      }
    } else {
      // Usar bairro do localFilters se houver
      if (localFilters.bairro) {
        newFilters.bairro = localFilters.bairro;
        setSearchLocation(localFilters.bairro);
      } else {
        newFilters.bairro = '';
        setSearchLocation('');
      }
    }
    
    if (localFilters.tipoImovel) {
      newFilters.tipo_imovel = localFilters.tipoImovel;
    } else if (filters.tipo_imovel) {
      newFilters.tipo_imovel = filters.tipo_imovel;
    }
    
    if (localFilters.valorMaximo) {
      newFilters.valorMaximo = localFilters.valorMaximo;
    } else if (filters.valorMaximo) {
      newFilters.valorMaximo = filters.valorMaximo;
    }
    
    if (localFilters.quartos) {
      newFilters.quartos_min = parseInt(localFilters.quartos);
    } else if (filters.quartos_min) {
      newFilters.quartos_min = filters.quartos_min;
    }
    
    if (localFilters.banheiros) {
      newFilters.banheiros_min = parseInt(localFilters.banheiros);
    } else if (filters.banheiros_min) {
      newFilters.banheiros_min = filters.banheiros_min;
    }
    
    if (localFilters.vagas) {
      newFilters.vagas_min = parseInt(localFilters.vagas);
    } else if (filters.vagas_min) {
      newFilters.vagas_min = filters.vagas_min;
    }
    
    if (localFilters.area) {
      newFilters.area_min = parseInt(localFilters.area);
    } else if (filters.area_min) {
      newFilters.area_min = filters.area_min;
    }
    
    if (localFilters.finalidade) {
      const finalidade = localFilters.finalidade.trim();
      if (finalidade === 'aluguel') {
        newFilters.apenas_aluguel = true;
      } else if (finalidade === 'venda') {
        newFilters.apenas_venda = true;
      } else if (finalidade === 'ambos') {
        newFilters.aluguel_e_venda = true;
      }
    }
    
    newFilters.page = 1;
    newFilters.limit = 100;
    
    setFilters(newFilters);
    searchImoveis(newFilters);
  };

  const handlePropertyClick = (imovel: any) => {
    console.log('Imóvel clicado:', imovel);
    setSelectedPropertyData(imovel);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPropertyData(null);
  };

  const handleFavorite = (imovel: any) => {
    console.log('Favoritar imóvel:', imovel);
    // Implementar funcionalidade de favoritar
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleFilterChange = (field: string, value: string) => {
    setLocalFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApplyFilters = () => {
    if (codigoFromUrl) return;

    const newFilters: PropertySearchFilters = {
      bairro: localFilters.bairro || '',
      estado: 'SC',
      tipo_imovel: localFilters.tipoImovel || undefined,
      valorMaximo: localFilters.valorMaximo || undefined,
      quartos_min: localFilters.quartos ? parseInt(localFilters.quartos) : undefined,
      banheiros_min: localFilters.banheiros ? parseInt(localFilters.banheiros) : undefined,
      vagas_min: localFilters.vagas ? parseInt(localFilters.vagas) : undefined,
      area_min: localFilters.area ? parseInt(localFilters.area) : undefined,
      page: 1,
      limit: 100,
    };
    
    // Só adicionar cidade se não for "Todas"
    if (localFilters.cidade && localFilters.cidade !== 'Todas') {
      newFilters.cidade = localFilters.cidade;
    }
    
    if (localFilters.finalidade) {
      const finalidade = localFilters.finalidade.trim();
      if (finalidade === 'aluguel') {
        newFilters.apenas_aluguel = true;
      } else if (finalidade === 'venda') {
        newFilters.apenas_venda = true;
      } else if (finalidade === 'ambos') {
        newFilters.aluguel_e_venda = true;
      }
    }

    setFilters(newFilters);
    searchImoveis(newFilters);
  };

  const handleClearFilters = () => {
    if (codigoFromUrl) return;

    const clearedFilters = {
      cidade: 'Todas',
      bairro: '',
      valorMaximo: '',
      quartos: '',
      banheiros: '',
      vagas: '',
      tipoImovel: '',
      area: '',
      finalidade: '',
    };

    setLocalFilters(clearedFilters);

    const newFilters: PropertySearchFilters = {
      estado: 'SC',
      page: 1,
      limit: 100,
    };

    setFilters(newFilters);
    searchImoveis(newFilters);
  };

  // Função para ordenar imóveis
  const sortImoveis = (imoveisToSort: any[], order: 'relevantes' | 'maior-valor' | 'menor-valor') => {
    const sorted = [...imoveisToSort];
    
    switch (order) {
      case 'maior-valor':
        return sorted.sort((a, b) => {
          // Calcular valor principal (priorizar venda se existir, senão aluguel)
          const valorA = a.valor_venda && a.valor_venda > 0 ? a.valor_venda : (parseFloat(a.valor_aluguel || '0'));
          const valorB = b.valor_venda && b.valor_venda > 0 ? b.valor_venda : (parseFloat(b.valor_aluguel || '0'));
          return valorB - valorA;
        });
      case 'menor-valor':
        return sorted.sort((a, b) => {
          // Calcular valor principal (priorizar venda se existir, senão aluguel)
          const valorA = a.valor_venda && a.valor_venda > 0 ? a.valor_venda : (parseFloat(a.valor_aluguel || '0'));
          const valorB = b.valor_venda && b.valor_venda > 0 ? b.valor_venda : (parseFloat(b.valor_aluguel || '0'));
          return valorA - valorB;
        });
      case 'relevantes':
      default:
        return sorted;
    }
  };

  // Determinar quais imóveis mostrar
  const baseImoveis = codigoFromUrl 
    ? (singleProperty ? [singleProperty] : [])
    : imoveis;
  
  // Filtrar imóveis por tab (alugar ou comprar)
  const filteredImoveis = baseImoveis.filter((imovel) => {
    if (codigoFromUrl) return true;
    
    if (activeTab === 'alugar') {
      return imovel.valor_aluguel && imovel.valor_aluguel > 0;
    } else {
      return imovel.valor_venda && imovel.valor_venda > 0;
    }
  });
  
  const displayImoveis = sortImoveis(filteredImoveis, sortOrder);
  
  const displayLoading = codigoFromUrl 
    ? singlePropertyLoading 
    : loading;

  const calculateTotalResults = () => {
    if (codigoFromUrl) {
      return singleProperty ? 1 : 0;
    }
    
    return displayImoveis.length;
  };
  
  // Calcular contagem de imóveis por tab
  const imoveisParaAlugar = baseImoveis.filter((imovel) => 
    imovel.valor_aluguel && imovel.valor_aluguel > 0
  ).length;
  
  const imoveisParaComprar = baseImoveis.filter((imovel) => 
    imovel.valor_venda && imovel.valor_venda > 0
  ).length;

  return (
    <div className="search-page">
      <div className="search-header-container">
        <SearchHeader 
          onSearch={handleSearch} 
          onBackToHome={onBackToHome}
          initialLocation={searchLocation}
          onToggleFilters={toggleFilters}
          showFilters={showFilters}
        />
      </div>

      {/* Seção completa de filtros colapsável */}
      <div className={`advanced-filters-container ${showFilters ? 'expanded' : 'collapsed'}`}>
        {showFilters && !codigoFromUrl && (
          <div className="advanced-filters-content">
            {/* Última busca */}
            <div className="last-search">
              <svg className="clock-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className="last-search-text">Balneário Camboriú, SC, Brasil</span>
            </div>

            {/* Cidade */}
            <div className="filter-field">
              <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <select
                className="filter-select"
                value={localFilters.cidade}
                onChange={(e) => handleFilterChange('cidade', e.target.value)}
              >
                <option value="Todas">Todas</option>
                <option value="Balneário Camboriú">Balneário Camboriú</option>
                <option value="Itajaí">Itajaí</option>
              </select>
            </div>

            {/* Bairro */}
            <div className="filter-field">
              <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <input
                type="text"
                placeholder="Centro, Praia dos Amores, Barra Sul..."
                className="filter-input"
                value={localFilters.bairro}
                onChange={(e) => handleFilterChange('bairro', e.target.value)}
              />
            </div>

            {/* Valor e Quartos */}
            <div className="filter-row">
              <div className="filter-field">
                <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2V6M12 18V22M6 12H2M22 12H18M19.7782 4.22183L16.9497 7.05025M7.05025 16.9497L4.22183 19.7782M19.7782 19.7782L16.9497 16.9497M7.05025 7.05025L4.22183 4.22183" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <select
                  className="filter-select"
                  value={localFilters.valorMaximo}
                  onChange={(e) => handleFilterChange('valorMaximo', e.target.value)}
                >
                  <option value="">Valor máximo</option>
                  <option value="1500">Até R$ 1.500</option>
                  <option value="2500">Até R$ 2.500</option>
                  <option value="4000">Até R$ 4.000</option>
                  <option value="6000">Até R$ 6.000</option>
                  <option value="10000">Até R$ 10.000</option>
                  <option value="15000">Até R$ 15.000</option>
                  <option value="20000">Até R$ 20.000</option>
                  <option value="30000">Até R$ 30.000</option>
                  <option value="50000">Até R$ 50.000</option>
                </select>
              </div>

              <div className="filter-field">
                <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2 12C2 9.79086 3.79086 8 6 8H18C20.2091 8 22 9.79086 22 12V20C22 20.5523 21.5523 21 21 21H19C18.4477 21 18 20.5523 18 20V16H6V20C6 20.5523 5.55228 21 5 21H3C2.44772 21 2 20.5523 2 20V12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <select
                  className="filter-select"
                  value={localFilters.quartos}
                  onChange={(e) => handleFilterChange('quartos', e.target.value)}
                >
                  <option value="">Quartos</option>
                  <option value="1">1 quarto</option>
                  <option value="2">2 quartos</option>
                  <option value="3">3 quartos</option>
                  <option value="4">4+ quartos</option>
                </select>
              </div>
            </div>

            {/* Filtros adicionais */}
            <div className="filter-row">
              <div className="filter-field">
                <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12C9 13.1046 8.10457 14 7 14C5.89543 14 5 13.1046 5 12C5 10.8954 5.89543 10 7 10C8.10457 10 9 10.8954 9 12Z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M4 20V12C4 8.68629 6.68629 6 10 6H14C17.3137 6 20 8.68629 20 12V20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <select
                  className="filter-select"
                  value={localFilters.banheiros}
                  onChange={(e) => handleFilterChange('banheiros', e.target.value)}
                >
                  <option value="">Banheiros</option>
                  <option value="1">1 banheiro</option>
                  <option value="2">2 banheiros</option>
                  <option value="3">3+ banheiros</option>
                </select>
              </div>

              <div className="filter-field">
                <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 9V7C5 5.89543 5.89543 5 7 5H17C18.1046 5 19 5.89543 19 7V9M5 9H19M5 9L3 12V19C3 19.5523 3.44772 20 4 20H5C5.55228 20 6 19.5523 6 19V17H18V19C18 19.5523 18.4477 20 19 20H20C20.5523 20 21 19.5523 21 19V12L19 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9 13H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <select
                  className="filter-select"
                  value={localFilters.vagas}
                  onChange={(e) => handleFilterChange('vagas', e.target.value)}
                >
                  <option value="">Vagas</option>
                  <option value="1">1 vaga</option>
                  <option value="2">2 vagas</option>
                  <option value="3">3+ vagas</option>
                </select>
              </div>
            </div>

            {/* Tipo de Imóvel */}
            <div className="filter-field">
              <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 12L5 10M5 10L12 3L19 10M5 10V20C5 20.5523 5.44772 21 6 21H9M19 10L21 12M19 10V20C19 20.5523 18.5523 21 18 21H15M9 21C9.55228 21 10 20.5523 10 20V16C10 15.4477 10.4477 15 11 15H13C13.5523 15 14 15.4477 14 16V20C14 20.5523 14.4477 21 15 21M9 21H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <select
                className="filter-select"
                value={localFilters.tipoImovel}
                onChange={(e) => handleFilterChange('tipoImovel', e.target.value)}
              >
                <option value="">Tipo de imóvel</option>
                <option value="apartamento">Apartamento</option>
                <option value="casa">Casa</option>
                <option value="kitnet">Kitnet</option>
                <option value="studio">Studio</option>
                <option value="loft">Loft</option>
                <option value="duplex">Duplex</option>
                <option value="cobertura">Cobertura</option>
                <option value="terreno">Terreno</option>
                <option value="comercial">Comercial</option>
                <option value="galpao">Galpão</option>
              </select>
            </div>

            {/* Área */}
            <div className="filter-field">
              <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 7V5C3 3.89543 3.89543 3 5 3H19C20.1046 3 21 3.89543 21 5V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M3 21V19C3 17.8954 3.89543 17 5 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M21 21L3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M21 17H18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M15 21V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M9 3V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <input
                type="number"
                placeholder="Área mínima (m²)"
                className="filter-input"
                value={localFilters.area}
                onChange={(e) => handleFilterChange('area', e.target.value)}
                min="0"
              />
            </div>

            {/* Finalidade */}
            <div className="filter-field">
              <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <select
                className="filter-select"
                value={localFilters.finalidade}
                onChange={(e) => handleFilterChange('finalidade', e.target.value)}
              >
                <option value="">Finalidade</option>
                <option value="aluguel">Aluguel</option>
                <option value="venda">Venda</option>
                <option value="ambos">Aluguel e Venda</option>
              </select>
            </div>

            {/* Ações */}
            <div className="filter-actions">
              <button
                className="search-action-btn primary"
                onClick={handleApplyFilters}
              >
                Buscar imóveis
              </button>
              <button
                className="search-action-btn secondary"
                onClick={handleClearFilters}
              >
                Limpar filtros
              </button>
            </div>
          </div>
        )}
      </div>
      
      <div className="search-content">
        {/* Tabs para Alugar e Comprar */}
        {!codigoFromUrl && (
          <div className="search-tabs-container">
            <button
              onClick={() => setActiveTab('alugar')}
              className={`search-tab ${activeTab === 'alugar' ? 'active' : ''}`}
            >
              <span>Para Alugar</span>
              <span className="search-tab-badge">{imoveisParaAlugar}</span>
            </button>
            <button
              onClick={() => setActiveTab('comprar')}
              className={`search-tab ${activeTab === 'comprar' ? 'active' : ''}`}
            >
              <span>Para Comprar</span>
              <span className="search-tab-badge">{imoveisParaComprar}</span>
            </button>
          </div>
        )}
        
        <SearchResults
          imoveis={displayImoveis}
          loading={displayLoading}
          totalResults={calculateTotalResults()}
          location={searchLocation}
          onPropertyClick={handlePropertyClick}
          onFavorite={handleFavorite}
          sortOrder={sortOrder}
          onSortChange={setSortOrder}
          activeTab={activeTab}
        />
      </div>
      
      {/* Modal de detalhes do imóvel */}
      {selectedPropertyData && (
        <PropertyDetailsModal 
          propertyId={selectedPropertyData.id} 
          propertyData={selectedPropertyData}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

